'use client';

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Tooltip } from '@mui/material';
import { CascadingMenuItem } from '@prismo-io/design-system';
import { useMe } from '@prismo-io/feature-auth/providers/me';
import { DownloadPdfUserButton } from '@prismo-io/feature-pdf';
import {
  LanguageCode,
  type ToolsOrganizationsT,
  UserAccessType,
  type UserAccessTypeT,
  type UsersT,
} from '@prismo-io/schemas';
import { type FC, useMemo } from 'react';

type Props = {
  user: Pick<UsersT, 'id' | 'full_name' | 'has_aura' | 'has_helio' | 'url'>;
  menuTitle?: string;
  needValidationIcon?: boolean;
  arrowIsFirst?: boolean;
  access: UserAccessTypeT;
  organization?: Pick<ToolsOrganizationsT, 'id' | 'email'>;
  anchorOrigin?: {
    horizontal: number | 'center' | 'left' | 'right';
    vertical: number | 'center' | 'bottom' | 'top';
  };
  transformOrigin?: {
    horizontal: number | 'center' | 'left' | 'right';
    vertical: number | 'center' | 'bottom' | 'top';
  };
  cvIsEnable?: boolean;
};

const talentPdfAccess: UserAccessTypeT[] = [
  UserAccessType.enum.TALENT,
  UserAccessType.enum.EXPERT_FREE,
  UserAccessType.enum.STARTER,
  UserAccessType.enum.LEAD,
  UserAccessType.enum.EXPERT,
];

const expertPdfAccess: UserAccessTypeT[] = [
  UserAccessType.enum.EXPERT_FREE,
  UserAccessType.enum.STARTER,
  UserAccessType.enum.LEAD,
  UserAccessType.enum.EXPERT,
];

export const PdfDownloadsMenu: FC<Props> = (props: Props) => {
  const {
    user,
    access,
    organization = undefined,
    menuTitle = t`Téléchargez les rapports`,
    needValidationIcon = false,
    arrowIsFirst = false,
    anchorOrigin = { horizontal: 'right', vertical: 'top' },
    transformOrigin = { horizontal: 'left', vertical: 'top' },
    cvIsEnable = false,
  } = props;

  const { i18n } = useLingui();
  const { locale = LanguageCode.Enum.fr } = i18n;

  const me = useMe();

  const isMe = useMemo(() => me && me.url === user.url, [me, user]);

  const withTalentPdf = useMemo(
    () => talentPdfAccess.includes(access) && !!organization,
    [access, organization]
  );

  const withExpertPdf = useMemo(
    () => expertPdfAccess.includes(access) && !!organization,
    [access, organization]
  );

  if (access === UserAccessType.enum.VISITOR) {
    return null;
  }

  return (
    <Tooltip
      placement="top"
      title={
        access === UserAccessType.enum.LEAD
          ? t`Rapports Talent et Expert disponibles en souscrivant à un abonnement PRISMO Tools`
          : null
      }
    >
      <Box component="div">
        <DownloadPdfUserButton
          organization={organization}
          users={[user]}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          config={{
            cv: { visible: cvIsEnable },
            talent: {
              visible:
                withTalentPdf ||
                access === UserAccessType.enum.LEAD ||
                access === UserAccessType.enum.EXPERT_FREE,
              disabled:
                access === UserAccessType.enum.LEAD ||
                access === UserAccessType.enum.EXPERT_FREE ||
                !user.has_aura,
            },
            expert: {
              visible: withExpertPdf || access === UserAccessType.enum.LEAD,
              disabled: access !== UserAccessType.enum.EXPERT || !user.has_aura,
            },
          }}
        >
          {(props: any) => (
            <CascadingMenuItem
              {...props}
              arrow={anchorOrigin.horizontal === 'right' ? 'right' : 'left'}
            >
              {isMe ? <Trans>Mes rapports</Trans> : <Trans>Rapports</Trans>}
            </CascadingMenuItem>
          )}
        </DownloadPdfUserButton>
      </Box>
    </Tooltip>
  );
};
